// Blockers — answers "what should I fix first to unblock billing?"
import { ExportCsvButton } from '../../components/ui';
import { downloadCsv } from '../../utils/csv.js';
import { useInfoModal, PageChrome, InfoModal, dqBadgeFor } from './Chrome.jsx';
import { fmtN, fmtH1, narrativeForBlockers, pctLabel, SUB_PAGES } from './helpers.js';
import { KpiCard, KpiGrid, SmartText } from './Widgets.jsx';

// Built here: the dashboard payload already carries every row this table shows.
const CSV_COLUMNS = [
  { key: 'ns_location_name', label: 'Location' },
  { key: 'site_code',        label: 'Site' },
  { key: 'gaps',             label: 'Blocked Records' },
  { key: 'gap_hours',        label: 'Blocked Hours' },
];

export function PageBlockers({ stats, nav }) {
  const info = useInfoModal();
  const helpEntry = SUB_PAGES.find((p) => p.id === 'blockers');
  const total = stats.total || 0;
  const gap = stats.gap || 0;
  const gapHrs = stats.gap_hours || 0;
  const blockedLocations = (stats.top_locations || []).filter((l) => (l.gaps || 0) > 0)
    .sort((a, b) => (b.gap_hours || 0) - (a.gap_hours || 0));

  return (
    <>
      <PageChrome
        title="Blockers"
        subtitle={`${fmtN(gap)} records blocked · ${fmtH1(gapHrs)} of work cannot be billed until fixed.`}
        onHome={() => nav.goSubPage('overview')}
        onBack={nav.goBack} canBack={nav.canBack}
        onInfo={info.onOpen}
        dqBadge={dqBadgeFor(stats, () => nav.goSubPage('data_quality'))}
      />

      <KpiGrid>
        <KpiCard label="Blocked Records"  tone="red"   value={gap}                  sub={`${pctLabel(gap, total)} of all records`}
          cta="Open gap records" onClick={() => nav.goRecords({ filter: 'gap' })} />
        <KpiCard label="Blocked Hours"    tone="red"   value={gapHrs} suffix="h"   sub="Worked but unbillable" />
        <KpiCard label="Locations Affected" tone="orange" value={blockedLocations.length} sub={`out of ${(stats.top_locations || []).length} top locations`} />
      </KpiGrid>

      <SmartText lines={narrativeForBlockers(stats)} />

      <div className="db-card">
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}>
          <div className="db-card-title">Locations with the most blocked work</div>
          {blockedLocations.length > 0 && (
            <ExportCsvButton
              onExport={() => downloadCsv('blocked-locations', blockedLocations, CSV_COLUMNS)}
              title="Download the blocked-locations table as CSV"
            />
          )}
        </div>
        <div className="db-card-sub">Sorted by blocked hours. Fixing the top location yields the biggest unblock.</div>
        {blockedLocations.length === 0 ? (
          <div className="db-empty-sub">No location has unblocked records right now.</div>
        ) : (
          <table className="db-table">
            <thead>
              <tr>
                <th>Location</th>
                <th>Site</th>
                <th className="num">Blocked records</th>
                <th className="num">Blocked hours</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {blockedLocations.map((l, i) => (
                <tr key={i}>
                  <td>{l.ns_location_name}</td>
                  <td>{l.site_code || '—'}</td>
                  <td className="num">{fmtN(l.gaps || 0)}</td>
                  <td className="num">{fmtH1(l.gap_hours || 0)}</td>
                  <td className="num">
                    <button type="button" className="btn btn-sm btn-primary"
                      onClick={() => nav.goRecords({ filter: 'gap', siteCode: l.site_code || '' })}>
                      Open →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="db-card">
        <div className="db-card-title">How to fix gap records</div>
        <ul style={{ paddingLeft: 18, fontSize: 13, lineHeight: 1.6 }}>
          <li>Open a gap record from Records → Gaps. The detail panel shows which mapping was missing.</li>
          <li>If the mapping is missing in NetSuite — add it in NetSuite, then re-sync (Pipeline → run on demand).</li>
          <li>If a labor metric is new — add it in Rules → Labor Metrics or wait for the next overnight ref sync.</li>
          <li>Re-process billing (Data Extraction → Reprocess Billing) to pick up the fix without waiting until tomorrow.</li>
        </ul>
      </div>

      <InfoModal open={info.open} onClose={info.onClose} title="About Blockers" body={helpEntry?.help} />
    </>
  );
}
